import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { ElectronicBillFormSchema, ItemComplete, Tax } from "./schemas/ElectronicBill"
import userMapper from './mappers/User/user.mapper'
import { User } from './schemas/User'
import entityMapper from './mappers/Entity/entity.mapper'
import { Entity } from './schemas/Entity'
import { SupportDocumentFormSchema } from './schemas/SupportDocument'
import html2pdf from 'html2pdf.js'

const Utils = {
  /** Returns whether the auth cookie access token exists and its exp is still in the future. */
  isAccessTokenValid: (): boolean => {
    const token = Cookies.get('auth')
    if (token === undefined || token === '') {
      return false
    }
    try {
      const decoded = jwtDecode<{ exp?: number }>(token)
      if (decoded.exp === undefined) {
        return false
      }
      const nowSeconds = Math.floor(Date.now() / 1000)
      return decoded.exp > nowSeconds
    } catch (error) {
      console.error('Failed to validate access token expiry', error)
      return false
    }
  },

  getUserByCookieAuth: () : User | undefined => {
    type JwtDecode = {
      data: {
        user: any
      }
    }
    const token = Cookies.get('auth')

    if (token === undefined) return undefined

    const tokenData = jwtDecode<JwtDecode>(token)
    const user = userMapper(tokenData.data.user)
    user.token = token
    return user
  },

  getEntityByCookieAuth: () : Entity | undefined => {
    type JwtDecode = {
      data: {
        entity: any
      }
    }
    const token = Cookies.get('auth')

    if (token === undefined) return undefined

    const tokenData = jwtDecode<JwtDecode>(token)
    const entity = entityMapper(tokenData.data.entity)
    return entity
  },

  removeCookieAuth: () => {
    Cookies.remove('auth')
  },

  /** Returns the max decimal places allowed for prices from env (default 2). */
  getMaxPriceDecimals: (): number => {
    const raw = process.env.REACT_APP_MAX_PRICE_DECIMALS
    if (raw === undefined || raw === '') {
      return 2
    }
    const parsed = parseInt(raw, 10)
    if (Number.isNaN(parsed) || parsed < 0) {
      return 2
    }
    return parsed
  },

  /** Rounds a monetary value to the configured max decimal places (half up). */
  roundToMaxDecimals: (value: number): number => {
    const decimals = Utils.getMaxPriceDecimals()
    const factor = Math.pow(10, decimals)
    return Math.round((value + Number.EPSILON) * factor) / factor
  },

  /** Returns whether the user enters tax-included prices (feature flag from env). */
  isPriceIncludingTaxEnabled: (): boolean => {
    const raw = process.env.REACT_APP_PRICE_INCLUDES_TAX
    if (raw === undefined || raw === '') {
      return false
    }
    const normalized = raw.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') {
      return true
    }
    return false
  },

  /** Extracts net unit price and tax from a tax-included gross unit price. */
  extractNetFromGrossPrice: (
    grossUnitPrice: number,
    quantity: number,
    percent: number
  ): { netUnitPrice: number; lineNet: number; taxAmount: number; taxableAmount: number } => {
    const grossUnit = Utils.roundToMaxDecimals(grossUnitPrice)
    const netUnit = Utils.roundToMaxDecimals(grossUnit / (1 + percent / 100))
    const lineNet = Utils.roundToMaxDecimals(quantity * netUnit)
    const grossLine = Utils.roundToMaxDecimals(quantity * grossUnit)
    const taxAmount = Utils.roundToMaxDecimals(grossLine - lineNet)

    return {
      netUnitPrice: netUnit,
      lineNet: lineNet,
      taxAmount: taxAmount,
      taxableAmount: lineNet
    }
  },

  buildItem: (form: ElectronicBillFormSchema, applyTaxes: boolean): ItemComplete => {
    let tax = {
      code: '',
      description: '',
      percent: 0,
      taxAmount: 0,
      taxableAmount: 0
    }

    let price = Utils.roundToMaxDecimals(form.currentPrice)
    let lineTotal = Utils.roundToMaxDecimals(form.currentQuantity * price)

    if (applyTaxes) {
      const useTaxIncludedPrice = Utils.isPriceIncludingTaxEnabled() && form.currentPercentTax > 0

      if (useTaxIncludedPrice) {
        const extracted = Utils.extractNetFromGrossPrice(
          form.currentPrice,
          form.currentQuantity,
          form.currentPercentTax
        )
        price = extracted.netUnitPrice
        lineTotal = extracted.lineNet
        tax = {
          ...form.selectedTax,
          percent: form.currentPercentTax,
          taxAmount: extracted.taxAmount,
          taxableAmount: extracted.taxableAmount
        }
      } else {
        tax = {
          ...form.selectedTax,
          percent: form.currentPercentTax,
          taxAmount: Utils.roundToMaxDecimals(lineTotal * form.currentPercentTax / 100),
          taxableAmount: lineTotal
        }
      }
    } else {
      tax = {
        code: '19',
        description: 'No aplica',
        percent: 0,
        taxAmount: 0,
        taxableAmount: lineTotal
      }
    }

    const item: ItemComplete = {
      ...form.currentItem,
      itemType: form.currentItemType,
      price: price,
      quantity: form.currentQuantity,
      total: lineTotal,
      taxes: [
        tax
      ]
    }

    const detailedDescription = form.currentDetailedDescription.trim()
    if (detailedDescription !== '') {
      item.detailedDescription = detailedDescription
    }

    return item
  },

  buildTaxesForElectronicBill: (items: ItemComplete[]) : Tax[] | [] => {
    let res: Tax[]| any[] = []
    items.forEach(item => {
      res = res.concat(item.taxes)
    })
    return res
  },

  buildItemForSupportDocument: (form: SupportDocumentFormSchema, applyTaxes: boolean): ItemComplete => {
    let tax = {
      code: '',
      description: '',
      percent: 0,
      taxAmount: 0,
      taxableAmount: 0
    }

    const price = Utils.roundToMaxDecimals(form.currentPrice)
    const lineTotal = Utils.roundToMaxDecimals(form.currentQuantity * price)

    if (applyTaxes) {
      tax = {
        ...form.selectedHoldingTax,
        percent: form.currentPercentHoldingTax,
        taxAmount: Utils.roundToMaxDecimals(lineTotal * form.currentPercentHoldingTax / 100),
        taxableAmount: lineTotal
      }
    } else {
      tax = {
        code: '1',
        description: 'Iva',
        percent: 0,
        taxAmount: 0,
        taxableAmount: lineTotal
      }
    }

    const item: ItemComplete = {
      ...form.currentItem,
      itemType: form.currentItemType,
      price: price,
      quantity: form.currentQuantity,
      total: lineTotal,
      taxes: [
        tax
      ]
    }

    const detailedDescription = form.currentDetailedDescription.trim()
    if (detailedDescription !== '') {
      item.detailedDescription = detailedDescription
    }

    return item
  },

  getIdFromUrl: (search: string) : string | undefined => {
    if (search !== undefined) {
      const regex = /=\s*(\d+)/
      const match = search.match(regex)

      if (match) {
        const document = match[1]
        return document
      }
    } else {
      return undefined
    }
  },

  getCodeFromUrl: (search: string): string | undefined => {
    if (search !== undefined) {
      const regex = /=\s*(\S+)/g;
      const match = search.match(regex);
      if (match) {
        const document = match[0].substring(1);
        return document;
      }
    } else {
      return undefined;
    }
  },

  getDateValuesFromUrl(url: string) {
    const regex = /date=([^&]+).*?toDate=([^&]+)/;
    const match = url.match(regex);

    if (match) {
      return {
        date: match[1],
        toDate: match[2]
      };
    }

    return null;
  },

  parseQueryString: (url: string) : { [key: string]: string }[] => {
    const queryString = url.split('?')[1]
    if (!queryString) {
      return []
    }
    const params = queryString.split('&')
    const result = []
    for (const param of params) {
      const [key, value] = param.split('=')
      result.push({ [key]: decodeURIComponent(value) })
    }
    return result
  },

  /** Returns the tax amount for a line item (first tax entry, or 0). */
  getItemTaxAmount: (item: { taxes?: { taxAmount: number }[] }): number => {
    if (item.taxes === undefined || item.taxes.length === 0) {
      return 0
    }
    return item.taxes[0].taxAmount ?? 0
  },

  /** Returns the final unit price including tax for a line item. */
  getItemFinalUnitPrice: (item: { price: number; quantity: number; total: number; taxes?: { taxAmount: number }[] }): number => {
    const taxAmount = Utils.getItemTaxAmount(item)
    const finalLineTotal = Utils.roundToMaxDecimals(item.total + taxAmount)
    if (item.quantity <= 0) {
      return Utils.roundToMaxDecimals(item.price)
    }
    return Utils.roundToMaxDecimals(finalLineTotal / item.quantity)
  },

  /** Returns the final line total including tax. */
  getItemFinalLineTotal: (item: { total: number; taxes?: { taxAmount: number }[] }): number => {
    const taxAmount = Utils.getItemTaxAmount(item)
    return Utils.roundToMaxDecimals(item.total + taxAmount)
  },

  formatNumber: (number: number): string => {
    const decimals = Utils.getMaxPriceDecimals()
    return number.toLocaleString('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  },

  /** Formats a price with thousands (.) and decimals (,) for COP display. */
  formatPriceCOP: (value: number | undefined): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '';
    const decimals = Utils.getMaxPriceDecimals()
    return Number(value).toLocaleString('es-CO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  },

  /** Parses a COP-formatted price string (1.234,56) into a number. */
  parseFormattedPriceCOP: (str: string): number | undefined => {
    if (!str || str.trim() === '') return undefined
    const normalized = str.replace(/\./g, '').replace(',', '.')
    const parsed = parseFloat(normalized)
    if (Number.isNaN(parsed)) return undefined
    return parsed
  },

  generatePDF: (htmlContent: string, filename: string, documentType: string = 'documento') => {
    try {
      const options = {
        margin: 0.5,
        filename: `${documentType}-${filename.replace(/\s+/g, '-')}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
      };

      html2pdf().set(options).from(htmlContent).save();
    } catch (error) {
      console.error(`Error generando PDF de ${documentType}:`, error);
      throw error;
    }
  },

  generateElectronicInvoicePDF: (htmlContent: string, invoiceNumber: string = 'factura') => {
    return Utils.generatePDF(htmlContent, invoiceNumber, 'factura-electronica');
  },

  generateSupportDocumentPDF: (htmlContent: string, documentNumber: string = 'documento-soporte') => {
    return Utils.generatePDF(htmlContent, documentNumber, 'documento-soporte');
  },

  generateCreditNotePDF: (htmlContent: string, documentNumber: string = 'nota-credito') => {
    return Utils.generatePDF(htmlContent, documentNumber, 'nota-credito');
  },

  generateCreditNoteSupportDocumentPDF: (htmlContent: string, documentNumber: string = 'nota-credito-documento-soporte') => {
    return Utils.generatePDF(htmlContent, documentNumber, 'nota-credito-documento-soporte');
  }
}

export default Utils